import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar'; // IMPORTAMOS EL MENÚ INTELIGENTE
import { Users, FileSpreadsheet, CheckCircle, Calendar, Home } from 'lucide-react';

const Matriculas = () => {
  const navigate = useNavigate();
  
  // 1. LISTAS DE DATOS (Lo que viene del Backend)
  const [alumnos, setAlumnos] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [aulas, setAulas] = useState([]);
  
  // 2. SELECCIONES DEL USUARIO (Lo que elige en los <select>)
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState('');
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState('');
  const [aulaSeleccionada, setAulaSeleccionada] = useState('');

  // 3. CARGA INICIAL: Traemos Alumnos y Años Escolares al entrar
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // 4. MAGIA EN CASCADA: Si elige un periodo, buscamos sus aulas
  useEffect(() => {
    if (periodoSeleccionado) {
      cargarAulasDelPeriodo(periodoSeleccionado);
    } else {
      setAulas([]); // Si deselecciona el año, vaciamos los salones
      setAulaSeleccionada('');
    }
  }, [periodoSeleccionado]);

  const cargarDatosIniciales = async () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    try {
      // Promise.all permite hacer ambas consultas al mismo tiempo para que cargue más rápido
      const [resAlumnos, resPeriodos] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/Alumnos`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_URL}/Periodos`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (resAlumnos.ok) setAlumnos(await resAlumnos.json());
      if (resPeriodos.ok) setPeriodos(await resPeriodos.json());
      
    } catch (error) {
      console.error("Error cargando datos base:", error);
    }
  };

  const cargarAulasDelPeriodo = async (periodoId) => {
    const token = localStorage.getItem('token');
    try {
      // Usamos la ruta "inteligente" que creaste en C#
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Aulas/PorPeriodo/${periodoId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setAulas(await response.json());
    } catch (error) {
      console.error("Error cargando aulas:", error);
    }
  };

  const handleMatricular = async (e) => {
    e.preventDefault();
    if (!alumnoSeleccionado || !periodoSeleccionado || !aulaSeleccionada) {
      return alert('Por favor, selecciona todas las opciones.');
    }

    const token = localStorage.getItem('token');
    
    const nuevaMatricula = {
      alumnoId: parseInt(alumnoSeleccionado),
      periodoId: parseInt(periodoSeleccionado),
      aulaId: parseInt(aulaSeleccionada),
      estado: "Regular",
      observaciones: "Matriculado vía Web",
      activo: true
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Matriculas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(nuevaMatricula)
      });

      if (response.ok) {
        setAlumnoSeleccionado('');
        setPeriodoSeleccionado('');
        setAulaSeleccionada('');
        setAulas([]); // Limpiamos para la siguiente matrícula
        alert('¡El estudiante ha sido matriculado oficialmente!');
      } else {
        alert('Hubo un problema al matricular. Revisa la consola.');
      }
    } catch (error) {
      console.error("Error al procesar matrícula:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      
      {/* AQUÍ INYECTAMOS EL MENÚ INTELIGENTE */}
      <Sidebar />

      {/* ÁREA PRINCIPAL */}
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="text-blue-600" /> Proceso de Matrícula
          </h1>
          <p className="text-slate-500 mt-1">Asigna un estudiante a un año escolar y salón específicos.</p>
        </header>

        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-100 max-w-3xl">
          <form onSubmit={handleMatricular} className="space-y-8">
            
            {/* SECCIÓN 1: ESTUDIANTE */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wide border-b pb-2">
                <Users size={18} className="text-blue-500"/> 1. Seleccionar Estudiante
              </label>
              <select 
                value={alumnoSeleccionado} 
                onChange={(e) => setAlumnoSeleccionado(e.target.value)}
                className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                required
              >
                <option value="">-- Buscar en la lista de alumnos registrados --</option>
                {alumnos.map(a => (
                  <option key={a.id} value={a.id}>{a.documentoIdentidad} - {a.apellidos}, {a.nombres}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* SECCIÓN 2: AÑO ESCOLAR */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wide border-b pb-2">
                  <Calendar size={18} className="text-blue-500"/> 2. Año Escolar
                </label>
                <select 
                  value={periodoSeleccionado} 
                  onChange={(e) => setPeriodoSeleccionado(e.target.value)}
                  className="w-full p-3.5 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                  required
                >
                  <option value="">-- Selecciona el Año --</option>
                  {periodos.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre} (Inicia: {new Date(p.fechaInicio).toLocaleDateString()})</option>
                  ))}
                </select>
              </div>

              {/* SECCIÓN 3: SALÓN DE CLASES */}
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 uppercase tracking-wide border-b pb-2">
                  <Home size={18} className="text-blue-500"/> 3. Salón / Aula
                </label>
                <select 
                  value={aulaSeleccionada} 
                  onChange={(e) => setAulaSeleccionada(e.target.value)}
                  disabled={!periodoSeleccionado} // Se desactiva si no ha elegido el año
                  className={`w-full p-3.5 border rounded-lg outline-none transition-all ${
                    periodoSeleccionado 
                    ? 'bg-slate-50 border-slate-300 focus:ring-2 focus:ring-blue-500 focus:bg-white cursor-pointer' 
                    : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  required
                >
                  <option value="">{periodoSeleccionado ? '-- Selecciona el Aula --' : 'Primero elige un Año Escolar'}</option>
                  {aulas.map(a => (
                    <option key={a.id} value={a.id}>{a.grado} - Sección "{a.seccion}" (Capacidad: {a.capacidad})</option>
                  ))}
                </select>
              </div>
            </div>

            {/* BOTÓN DE ACCIÓN */}
            <div className="pt-4 border-t">
              <button 
                type="submit" 
                className="w-full md:w-auto ml-auto bg-blue-600 text-white px-8 py-3.5 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 shadow-md"
              >
                <CheckCircle size={20} /> Procesar Matrícula Oficial
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Matriculas;